import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useSocket } from "components";
import { migrateCardsToGrid } from "components/Report-generator/utils/gridUtils";
import { Channel } from "pusher-js";
import { useReportsActions } from "store";
import type {
  CanvasCard,
  Currency,
  DateRangeValue,
  Filters,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  TemplateContentSummary,
  View
} from "store/types";

function buildContentSummary(
  cards: CanvasCard[],
  orientation: ReportOrientation,
  currency: Currency,
  period: DateRangeValue | null,
  filters: Filters
): TemplateContentSummary {
  return {
    card_count: cards.length,
    orientation,
    currency,
    period,
    filter_investors: filters.investors,
    filter_asset_classes: filters.assetClasses
  };
}

interface UseViewTemplateProps {
  filters: Filters;
  cards: CanvasCard[];
  period: DateRangeValue | null;
  currency: Currency;
  reportMeta: ReportMeta;
  pages: ReportPage[];
  orientation: ReportOrientation;
  logoFile: File | null;
  setCards: (cards: CanvasCard[]) => void;
  setFilters: (filters: Filters) => void;
  setLogoFile: (file: File | null) => void;
  setPeriod: (period: DateRangeValue | null) => void;
  setCurrency: (currency: Currency) => void;
  setReportMeta: React.Dispatch<React.SetStateAction<ReportMeta>>;
  setPages: (pages: ReportPage[]) => void;
  setOrientation: (orientation: ReportOrientation) => void;
  setActiveViewId: (id: string | null) => void;
  setViews: (views: View[]) => void;
}

interface UseViewTemplateReturn {
  views: View[];
  loadingViews: boolean;
  savingView: boolean;
  loadingView: boolean;
  fetchViews: () => Promise<void>;
  saveView: (name: string, description?: string) => Promise<void>;
  updateView: (name?: string, description?: string) => Promise<void>;
  loadView: (view: View) => Promise<void>;
  deleteView: (id: string) => Promise<void>;
  currentViewId: string | null;
}

export const useViewTemplate = ({
  filters,
  cards,
  period,
  currency,
  reportMeta,
  pages,
  orientation,
  logoFile,
  setCards,
  setFilters,
  setLogoFile,
  setPeriod,
  setCurrency,
  setReportMeta,
  setPages,
  setOrientation,
  setActiveViewId,
  setViews
}: UseViewTemplateProps): UseViewTemplateReturn => {
  const {
    getReportTemplatesAction,
    getReportTemplateAction,
    saveReportTemplateAction,
    updateReportTemplateAction,
    deleteReportTemplateAction
  } = useReportsActions();

  const socket = useSocket();

  const [views, setLocalViews] = useState<View[]>([]);
  const [loadingViews, setLoadingViews] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const [currentViewId, setCurrentViewId] = useState<string | null>(null);
  const [loadedLogoUrl, setLoadedLogoUrl] = useState<string | null>(null);
  const channelRef = useRef<Channel | null>(null);

  useEffect(() => {
    if (channelRef.current) {
      channelRef.current.unbind_all();
      socket.unsubscribe(channelRef.current.name);
      channelRef.current = null;
    }

    if (!currentViewId) return;

    const channelName = `private-report-template-${currentViewId}`;
    const channel = socket.subscribe(channelName);
    channelRef.current = channel;

    channel.bind("file.checked", (data: { type: "logo" | "background"; file_url: string }) => {
      if (data.type === "logo" && data.file_url) {
        setReportMeta((prev) => ({ ...prev, logo: data.file_url }));
        setLoadedLogoUrl(data.file_url);
        setLogoFile(null);
      }
    });

    return () => {
      channel.unbind_all();
      socket.unsubscribe(channelName);
      channelRef.current = null;
    };
  }, [currentViewId, socket, setReportMeta, setLogoFile]);

  const fetchViews = useCallback(async () => {
    setLoadingViews(true);
    try {
      const data = await getReportTemplatesAction();
      const mappedViews: View[] = data.map((item) => {
        const s = item.content_summary ? (JSON.parse(item.content_summary) as TemplateContentSummary) : null;

        return {
          id: item.id.toString(),
          name: item.name,
          description: item.description ?? "",
          filters: {
            investors: s?.filter_investors ?? [],
            assetClasses: s?.filter_asset_classes ?? []
          },
          cards: Array.from({ length: s?.card_count ?? 0 }) as CanvasCard[],
          period: s?.period ?? null,
          currency: (s?.currency ?? "USD") as Currency,
          reportMeta: { title: "", subtitle: "", asOfDate: "", preparedBy: "", logo: "" },
          pages: [],
          orientation: (s?.orientation ?? "portrait") as ReportOrientation
        };
      });
      setLocalViews(mappedViews);
      setViews(mappedViews);
    } catch (error) {
      console.error("Failed to fetch views:", error);
      toast.error("Failed to load views");
    } finally {
      setLoadingViews(false);
    }
  }, [getReportTemplatesAction, setViews]);

  const saveView = useCallback(
    async (name: string, description?: string) => {
      setSavingView(true);
      try {
        const contentPayload = {
          filters,
          cards,
          period,
          currency,
          reportMeta: { ...reportMeta, logo: "" },
          pages,
          orientation
        };

        const isUpdate = currentViewId !== null;

        const payload: Record<string, unknown> = {
          name,
          description: description ?? "",
          content: JSON.stringify(contentPayload),
          content_summary: JSON.stringify(buildContentSummary(cards, orientation, currency, period, filters)),
          report_title: reportMeta.title
        };

        if (isUpdate) {
          if (logoFile) {
            payload.logo = logoFile;
          } else if (loadedLogoUrl && !reportMeta.logo) {
            payload.logo_url = "";
          } else if (reportMeta.logo && reportMeta.logo !== loadedLogoUrl) {
            payload.logo_url = reportMeta.logo;
          }
        } else {
          if (logoFile) {
            payload.logo = logoFile;
          } else if (reportMeta.logo) {
            payload.logo_url = reportMeta.logo;
          }
        }

        const result = isUpdate
          ? await updateReportTemplateAction(
              parseInt(currentViewId!, 10),
              payload as unknown as Parameters<typeof updateReportTemplateAction>[1]
            )
          : await saveReportTemplateAction(
              payload as unknown as Parameters<typeof saveReportTemplateAction>[0]
            );

        const logoFileFromServer = result.files?.find((f) => f.type === "logo");
        if (logoFileFromServer?.file_url) {
          setReportMeta((prev) => ({ ...prev, logo: logoFileFromServer.file_url }));
          setLoadedLogoUrl(logoFileFromServer.file_url);
          setLogoFile(null);
        }

        const savedView: View = {
          id: (result?.id ?? Date.now()).toString(),
          name,
          description: description ?? "",
          filters,
          cards,
          period,
          currency,
          reportMeta,
          pages,
          orientation
        };

        setLocalViews((prev) => {
          const next = isUpdate
            ? prev.map((v) => (v.id === currentViewId ? savedView : v))
            : [...prev, savedView];
          setViews(next);
          return next;
        });
        setActiveViewId(savedView.id);
        setCurrentViewId(savedView.id);
        toast.success(isUpdate ? "View updated successfully" : "View saved successfully");
      } catch (error) {
        console.error("Failed to save view:", error);
        toast.error("Failed to save view");
      } finally {
        setSavingView(false);
      }
    },
    [
      filters,
      cards,
      period,
      currency,
      reportMeta,
      pages,
      orientation,
      logoFile,
      loadedLogoUrl,
      currentViewId,
      saveReportTemplateAction,
      updateReportTemplateAction,
      setViews,
      setActiveViewId,
      setReportMeta,
      setLogoFile
    ]
  );

  const updateView = useCallback(
    async (newName?: string, description?: string) => {
      if (!currentViewId) return;
      setSavingView(true);
      try {
        const contentPayload = {
          filters,
          cards,
          period,
          currency,
          reportMeta: { ...reportMeta, logo: "" },
          pages,
          orientation
        };

        const currentView = views.find((v) => v.id === currentViewId);
        const name = newName ?? currentView?.name ?? "Untitled";

        const resolvedDescription = description ?? currentView?.description ?? "";

        const payload: Record<string, unknown> = {
          name,
          description: resolvedDescription,
          content: JSON.stringify(contentPayload),
          content_summary: JSON.stringify(buildContentSummary(cards, orientation, currency, period, filters)),
          report_title: reportMeta.title
        };

        if (logoFile) {
          payload.logo = logoFile;
        } else if (loadedLogoUrl && !reportMeta.logo) {
          payload.logo_url = "";
        } else if (reportMeta.logo && reportMeta.logo !== loadedLogoUrl) {
          payload.logo_url = reportMeta.logo;
        }

        const result = await updateReportTemplateAction(
          parseInt(currentViewId, 10),
          payload as unknown as Parameters<typeof updateReportTemplateAction>[1]
        );

        const logoFileFromServer = result.files?.find((f) => f.type === "logo");
        if (logoFileFromServer?.file_url) {
          setReportMeta((prev) => ({ ...prev, logo: logoFileFromServer.file_url }));
          setLoadedLogoUrl(logoFileFromServer.file_url);
          setLogoFile(null);
        }

        setLocalViews((prev) => {
          const next = prev.map((v) =>
            v.id === currentViewId
              ? { ...v, name, description: resolvedDescription, filters, cards, period, currency, reportMeta, pages, orientation }
              : v
          );
          setViews(next);
          return next;
        });

        toast.success("View updated successfully");
      } catch (error) {
        console.error("Failed to update view:", error);
        toast.error("Failed to update view");
      } finally {
        setSavingView(false);
      }
    },
    [
      filters,
      cards,
      period,
      currency,
      reportMeta,
      pages,
      orientation,
      logoFile,
      loadedLogoUrl,
      currentViewId,
      views,
      updateReportTemplateAction,
      setViews,
      setReportMeta,
      setLogoFile
    ]
  );

  const loadView = useCallback(
    async (view: View) => {
      setLoadingView(true);
      try {
        const template = await getReportTemplateAction(parseInt(view.id, 10));

        const parsedContent = typeof template.content === "string" ? JSON.parse(template.content) : template.content;

        if (parsedContent) {
          if (parsedContent.filters) setFilters(parsedContent.filters);
          if (parsedContent.cards) setCards(migrateCardsToGrid(parsedContent.cards));
          if (parsedContent.period) setPeriod(parsedContent.period);
          if (parsedContent.currency) setCurrency(parsedContent.currency);
          if (parsedContent.reportMeta) {
            setReportMeta({ ...parsedContent.reportMeta, logo: "" });
          }
          if (parsedContent.pages) setPages(parsedContent.pages);
          if (parsedContent.orientation) setOrientation(parsedContent.orientation);
        }

        if (template.report_title) {
          setReportMeta((prev) => ({ ...prev, title: template.report_title || "" }));
        }

        const logoFile = template.files?.find((f) => f.type === "logo");
        if (logoFile?.file_url) {
          setReportMeta((prev) => ({ ...prev, logo: logoFile.file_url }));
          setLoadedLogoUrl(logoFile.file_url);
        } else {
          setLoadedLogoUrl(null);
        }

        setLogoFile(null);

        setActiveViewId(view.id);
        setCurrentViewId(view.id);
        toast.success(`View "${template.name}" loaded`);
      } catch (error) {
        console.error("Failed to load view:", error);
        toast.error("Failed to load view");
      } finally {
        setLoadingView(false);
      }
    },
    [
      getReportTemplateAction,
      setFilters,
      setCards,
      setPeriod,
      setCurrency,
      setReportMeta,
      setPages,
      setOrientation,
      setActiveViewId,
      setLogoFile
    ]
  );

  const deleteView = useCallback(
    async (id: string) => {
      try {
        await deleteReportTemplateAction(parseInt(id, 10));
        setLocalViews((prev) => {
          const next = prev.filter((v) => v.id !== id);
          setViews(next);
          return next;
        });
        if (currentViewId === id) {
          setCurrentViewId(null);
          setActiveViewId(null);
          setLoadedLogoUrl(null);
        }
        toast.success("View deleted");
      } catch (error) {
        console.error("Failed to delete view:", error);
        toast.error("Failed to delete view");
      }
    },
    [deleteReportTemplateAction, currentViewId, setViews, setActiveViewId]
  );

  useEffect(() => {
    void fetchViews();
  }, [fetchViews]);

  return {
    views,
    loadingViews,
    savingView,
    loadingView,
    fetchViews,
    saveView,
    updateView,
    loadView,
    deleteView,
    currentViewId
  };
};
